"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

// Kick off the geojson fetch as soon as this module loads — but ONLY if
// the device is actually going to render the globe. On phones, on
// `?no-3d`, or with OS reduced-motion enabled, PourquoiGlobeBg returns
// null and the fetch (~230 KB of JSON) would be pure waste.
//
// Self-hosted under /public/data/ — fetching from raw.githubusercontent.com
// at runtime was blocked by our CSP `connect-src` policy, broke offline-ish
// previews, and added a third-party-domain dependency to the critical path.
const GEOJSON_URL = "/data/ne_110m_land.json";
function shouldPreloadGeoJson(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.has("no-3d")) return false;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
  if (window.innerWidth < 768) return false;
  return true;
}
const geoJsonPromise: Promise<any> | null = shouldPreloadGeoJson()
  ? fetch(GEOJSON_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("geojson load failed"))))
      .catch(() => null)
  : null;

// Pre-compute the per-continent dot grid as soon as the geojson is in.
// Without this the ~10k point-in-polygon tests run synchronously the
// first time the globe's IntersectionObserver triggers — which freezes
// the page right when the user scrolls from Studio into "Pourquoi
// Kessem". Doing it eagerly at module load means the work has already
// finished by the time the user scrolls there.
interface DotData {
  lng: number;
  lat: number;
}

function pointInPolygon(
  point: [number, number],
  polygon: number[][],
): boolean {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if (
      yi > y !== yj > y &&
      x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInFeature(point: [number, number], feature: any): boolean {
  const geometry = feature.geometry;
  if (geometry.type === "Polygon") {
    const coordinates = geometry.coordinates;
    if (!pointInPolygon(point, coordinates[0])) return false;
    for (let i = 1; i < coordinates.length; i++) {
      if (pointInPolygon(point, coordinates[i])) return false;
    }
    return true;
  } else if (geometry.type === "MultiPolygon") {
    for (const polygon of geometry.coordinates) {
      if (pointInPolygon(point, polygon[0])) {
        let inHole = false;
        for (let i = 1; i < polygon.length; i++) {
          if (pointInPolygon(point, polygon[i])) {
            inHole = true;
            break;
          }
        }
        if (!inHole) return true;
      }
    }
    return false;
  }
  return false;
}

function generateDotsInPolygon(
  feature: any,
  dotSpacing = 16,
): [number, number][] {
  const dots: [number, number][] = [];
  const bounds = d3.geoBounds(feature);
  const [[minLng, minLat], [maxLng, maxLat]] = bounds;
  const stepSize = dotSpacing * 0.08;
  for (let lng = minLng; lng <= maxLng; lng += stepSize) {
    for (let lat = minLat; lat <= maxLat; lat += stepSize) {
      const point: [number, number] = [lng, lat];
      if (pointInFeature(point, feature)) dots.push(point);
    }
  }
  return dots;
}

const worldDataPromise: Promise<{
  landFeatures: any;
  allDots: DotData[];
} | null> | null =
  typeof window !== "undefined" && geoJsonPromise
    ? geoJsonPromise.then((landFeatures) => {
        if (!landFeatures) return null;
        const allDots: DotData[] = [];
        landFeatures.features.forEach((feature: any) => {
          const dots = generateDotsInPolygon(feature, 16);
          dots.forEach(([lng, lat]) => allDots.push({ lng, lat }));
        });
        return { landFeatures, allDots };
      })
    : null;

interface RotatingEarthProps {
  width?: number;
  height?: number;
  className?: string;
  /**
   * When true: render ONLY the land polygons filled in solid black on
   * a fully transparent canvas. No sphere, no graticule, no dots, no
   * outlines. Used as a section backdrop where only the continents
   * should be visible.
   */
  landOnly?: boolean;
}

export default function RotatingEarth({
  width = 800,
  height = 600,
  className = "",
  landOnly = false,
}: RotatingEarthProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Defer the entire d3 pipeline (fetch geojson + dot generation +
  // d3.timer animation loop) until the canvas is actually in viewport.
  // Otherwise the timer competes with the homepage's main-thread cycles
  // throughout the user's session — even while they're still reading
  // the Hero, the globe was already running its full-rate redraw loop.
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;
    // Toggle inView in BOTH directions — previously the observer only
    // flipped to true on first intersection and disconnected, so the
    // d3.timer kept burning frames at 60 fps for the rest of the
    // session even when the user had scrolled past Pourquoi.
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "200px" },
    );
    observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    if (!context) return;

    const containerWidth = Math.min(width, window.innerWidth - 40);
    const containerHeight = Math.min(height, window.innerHeight - 100);
    const radius = Math.min(containerWidth, containerHeight) / 2.5;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = containerWidth * dpr;
    canvas.height = containerHeight * dpr;
    canvas.style.width = `${containerWidth}px`;
    canvas.style.height = `${containerHeight}px`;
    context.scale(dpr, dpr);

    const projection = d3
      .geoOrthographic()
      .scale(radius)
      .translate([containerWidth / 2, containerHeight / 2])
      .clipAngle(90);

    const path = d3.geoPath().projection(projection).context(context);

    let allDots: DotData[] = [];
    let landFeatures: any;

    const render = () => {
      context.clearRect(0, 0, containerWidth, containerHeight);
      const currentScale = projection.scale();
      const scaleFactor = currentScale / radius;

      // landOnly mode: render the dotted-globe wireframe on a fully
      // transparent canvas, using DARK strokes/dots so the globe reads
      // against a light section background. No black sphere fill.
      if (landOnly) {
        if (landFeatures) {
          // Graticule (lat/lon grid) in faint dark lines.
          const graticule = d3.geoGraticule();
          context.beginPath();
          path(graticule());
          context.strokeStyle = "#1a1a1a";
          context.lineWidth = 1 * scaleFactor;
          context.globalAlpha = 0.18;
          context.stroke();
          context.globalAlpha = 1;

          // Land outlines (very faint).
          context.beginPath();
          landFeatures.features.forEach((feature: any) => path(feature));
          context.strokeStyle = "#1a1a1a";
          context.lineWidth = 1 * scaleFactor;
          context.globalAlpha = 0.25;
          context.stroke();
          context.globalAlpha = 1;

          // Continent dots in dark gray.
          allDots.forEach((dot) => {
            const projected = projection([dot.lng, dot.lat]);
            if (
              projected &&
              projected[0] >= 0 &&
              projected[0] <= containerWidth &&
              projected[1] >= 0 &&
              projected[1] <= containerHeight
            ) {
              context.beginPath();
              context.arc(
                projected[0],
                projected[1],
                1.2 * scaleFactor,
                0,
                2 * Math.PI,
              );
              context.fillStyle = "#222222";
              context.fill();
            }
          });
        }
        return;
      }

      context.beginPath();
      context.arc(
        containerWidth / 2,
        containerHeight / 2,
        currentScale,
        0,
        2 * Math.PI,
      );
      context.fillStyle = "#000000";
      context.fill();
      context.strokeStyle = "#ffffff";
      context.lineWidth = 2 * scaleFactor;
      context.stroke();

      if (landFeatures) {
        const graticule = d3.geoGraticule();
        context.beginPath();
        path(graticule());
        context.strokeStyle = "#ffffff";
        context.lineWidth = 1 * scaleFactor;
        context.globalAlpha = 0.25;
        context.stroke();
        context.globalAlpha = 1;

        context.beginPath();
        landFeatures.features.forEach((feature: any) => path(feature));
        context.strokeStyle = "#ffffff";
        context.lineWidth = 1 * scaleFactor;
        context.stroke();

        allDots.forEach((dot) => {
          const projected = projection([dot.lng, dot.lat]);
          if (
            projected &&
            projected[0] >= 0 &&
            projected[0] <= containerWidth &&
            projected[1] >= 0 &&
            projected[1] <= containerHeight
          ) {
            context.beginPath();
            context.arc(
              projected[0],
              projected[1],
              1.2 * scaleFactor,
              0,
              2 * Math.PI,
            );
            context.fillStyle = "#999999";
            context.fill();
          }
        });
      }
    };

    const loadWorldData = async () => {
      try {
        setIsLoading(true);
        // worldDataPromise already finished both the geojson fetch AND
        // the per-continent dot-grid computation at module-load time, so
        // here we just receive the precomputed payload — no synchronous
        // ~300 ms point-in-polygon burn on the main thread when the user
        // scrolls into the "Pourquoi Kessem" section.
        const data = worldDataPromise ? await worldDataPromise : null;
        if (!data) throw new Error("Failed to load land data");
        landFeatures = data.landFeatures;
        allDots = data.allDots;
        render();
        setIsLoading(false);
      } catch {
        setError("Failed to load land map data");
        setIsLoading(false);
      }
    };

    const rotation: [number, number] = [0, 0];
    const rotationSpeed = 0.5;

    const rotate = () => {
      rotation[0] += rotationSpeed;
      projection.rotate(rotation);
      render();
    };

    const rotationTimer = d3.timer(rotate);
    loadWorldData();

    return () => {
      rotationTimer.stop();
    };
  }, [inView, width, height, landOnly]);

  if (error) {
    return (
      <div
        className={`flex items-center justify-center bg-black rounded-2xl p-8 ${className}`}
      >
        <p className="text-red-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-auto"
        style={{ maxWidth: "100%", height: "auto" }}
      />
    </div>
  );
}
